import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Spinner,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { UPDATE_CHEAPER_PRICE } from '../../../../graphQL/mutations';
import { GET_CHEAPER_PRICE } from '../../../../graphQL/queries';
import { PrecoMaisBarato } from '../../types';

const formatarDataHora = (data: string | null) =>
  data ? new Date(data).toLocaleString('pt-BR') : 'nunca salvo';

/** Liga/desliga o card "Preço mais barato" no perfil do app (por tenant). */
const Main: FC = () => {
  const toast = useToast();
  const { data, loading, error, refetch } = useQuery<{
    cheaperPrice: PrecoMaisBarato;
  }>(GET_CHEAPER_PRICE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [atualizar, { loading: salvando }] = useMutation<{
    updateCheaperPrice: PrecoMaisBarato;
  }>(UPDATE_CHEAPER_PRICE, {
    refetchQueries: [{ query: GET_CHEAPER_PRICE }],
    awaitRefetchQueries: true,
  });

  const salvo = data?.cheaperPrice;
  const [ativo, setAtivo] = useState(false);

  // quando a query volta com dados novos (ex.: depois de salvar), a chave reflete o salvo
  useEffect(() => {
    if (salvo) setAtivo(salvo.active);
  }, [salvo]);

  const salvar = async () => {
    try {
      await atualizar({ variables: { active: ativo } });
      toast({
        title: 'Sucesso!',
        description: 'Preço mais barato atualizado',
        status: 'success',
        position: 'top-right',
      });
    } catch (erro: any) {
      toast({
        title: 'Opss!!',
        description:
          erro?.message || 'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const renderConteudo = () => {
    if (loading && !data) {
      return (
        <Flex justify="center" py={16}>
          <Spinner color="brand.500" size="xl" />
        </Flex>
      );
    }

    if (error && !data) {
      return (
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          <AlertDescription flex={1}>
            {error.message || 'Não foi possível carregar a configuração.'}
          </AlertDescription>
          <Button size="sm" onClick={() => refetch()}>
            Tentar de novo
          </Button>
        </Alert>
      );
    }

    return (
      <Box
        as="section"
        bg="white"
        border="1px solid #D6E8FF"
        borderRadius="12px"
        p={6}
        w="100%"
        maxW="720px"
      >
        <Flex align="center" gap={3} flexWrap="wrap" mb={6}>
          <Badge
            colorScheme={salvo?.active ? 'green' : 'gray'}
            fontSize="md"
            px={3}
            py={1}
          >
            {salvo?.active ? 'No ar' : 'Desligado'}
          </Badge>
          <Text color="gray.600" fontSize="sm">
            Última alteração: {formatarDataHora(salvo?.updated_at ?? null)}
          </Text>
        </Flex>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="cheaper-price-active" mb="0">
            Exibir no app
          </FormLabel>
          <Switch
            id="cheaper-price-active"
            size="lg"
            colorScheme="brand"
            isChecked={ativo}
            onChange={evento => setAtivo(evento.target.checked)}
          />
        </FormControl>
        <Text color="gray.600" fontSize="sm" mt={2}>
          Desligado, o card &quot;Preço mais barato&quot; some do perfil do app.
        </Text>

        <Flex justify="flex-end" mt={6}>
          <Button
            colorScheme="brand"
            onClick={salvar}
            isLoading={salvando}
            isDisabled={!salvo || ativo === salvo.active}
          >
            Salvar
          </Button>
        </Flex>
      </Box>
    );
  };

  return (
    <Flex
      flexDir="column"
      w="100%"
      minW={0}
      minH={0}
      h="100%"
      p={{ base: 4, lg: 8 }}
      overflowY="auto"
      overflowX="hidden"
      backgroundColor="#F9F9F9"
      sx={{ '& > *': { flexShrink: 0 } }}
    >
      <Heading mb={2} color="#469BFF" fontFamily="Poppins">
        Preço mais barato
      </Heading>
      <Text mb={6} color="gray.600">
        Exibição do card &quot;Preço mais barato&quot; no perfil do app
      </Text>
      {renderConteudo()}
    </Flex>
  );
};

export default Main;
